import React, { FC, Fragment, useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useDispatch, useSelector } from "react-redux";
import { deAuthenticate } from "store/auth/actions";
import { clearUserData } from "store/user/actions";
import BrandLogo from "svg/logo-autotrack-current.svg";
import SearchIcon from "svg/search.svg";
import DropdownIcon from "svg/arrow-down-nav.svg";
import StarIcon from "svg/star-outline.svg";
import { Badge } from "components/Badge";
import { useCookies } from "lib/cookie-monster";
import { menuItems } from "components/Navigation/constants";
import { ArrowRight } from "ui/atoms/icons/arrow-right";
import {
    NavContainer,
    HeaderLogo,
    Dropdown,
    Wrapper,
    MenuHeader,
    DropdownMenu,
    DropdownItem,
    DropdownLink,
    FavoriteLink,
    LogoWrapper,
    MenusWrapper,
    AccountsContainer,
    MobileMenu,
    MobileDropdownMenuHeader,
    MobileDropdownMenu,
    MobileDropdownItem,
    NavStyledArrow,
    MobileAccountsBtnWrapper,
    MobileAuthButton,
    MobileHRLine,
    HeaderLogoMobile,
    DesktopHRLine,
    MobileMenuContainer,
    MobileMenuMainWrapper,
    Divider,
    DownloadAppContainer,
    StyledLink,
    IconWrapper,
    LinkText,
    DownloadIconTextWrapper
} from "./Elements/Desktop";
import { StyledIcon, HamburgerMenuContainer, HamburgerMenu } from "./Elements/Mobile";

export const GetLoginLogout = (): JSX.Element => {
    const { authentication: { token = "" } = {} } = useSelector<any, any>((state) => state);
    const { setCookie, removeCookie } = useCookies();
    const dispatch = useDispatch();

    const handleLogout = () => {
        dispatch(clearUserData());
        dispatch(deAuthenticate());
        removeCookie("st");
    };

    const isDev = process.env.NODE_ENV === "development";
    if (token) {
        if (isDev) {
            return (
                <DropdownItem>
                    <DropdownLink href="/logout-local" rel="nofollow" onClick={handleLogout}>
                        Uitloggen
                    </DropdownLink>
                </DropdownItem>
            );
        }
        return (
            <DropdownItem>
                <DropdownLink
                    href="/mijn-autotrack/loguit"
                    rel="nofollow"
                    onClick={() => setCookie("uLi", "false")}
                >
                    Uitloggen
                </DropdownLink>
            </DropdownItem>
        );
    }
    return (
        <DropdownItem>
            <DropdownLink href="/login" rel="nofollow">
                Inloggen
            </DropdownLink>
        </DropdownItem>
    );
};

interface NavigationProps {
    toggle?: () => void;
}

export const Navigation: FC<NavigationProps> = ({ toggle }) => {
    const { cookies } = useCookies();
    const [activeMenu, setActiveMenu] = useState<string | null>(null);
    const [hamburgerMenuClicked, sethamburgerMenuClicked] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    const showDropdown = (selectedDropdown: string) => {
        setIsOpen(!isOpen);
        activeMenu !== selectedDropdown ? setActiveMenu(selectedDropdown) : setActiveMenu(null);
    };

    const { favourites: { favouritesIdList = [], awaitingResponse = [] } = {} } = useSelector<
        any,
        any
    >((state) => state);

    const toggleHamburgerMenu = () => {
        hamburgerMenuClicked ? sethamburgerMenuClicked(false) : sethamburgerMenuClicked(true);
    };

    const activateHamburgerMenu = (): void => {
        toggleHamburgerMenu();
        if (typeof toggle === "function") {
            toggle();
        }
    };

    /** We use this to check if a user clicks outside of the dropdown menu,
     *  if so we set the value for dropDownItemClicked to an empty string
     *  in order to collapse the dropdown.
     */
    const useClickOutsideMenu = () => {
        const menuHeaderRef = useRef<any>();

        const handleClickOutside = ({ target }: any) => {
            if (menuHeaderRef.current && !menuHeaderRef?.current?.contains(target)) {
                setActiveMenu(null);
            }
        };

        useEffect(() => {
            menuHeaderRef.current?.addEventListener("click", handleClickOutside, true);
            return () => {
                menuHeaderRef.current?.removeEventListener("click", handleClickOutside, true);
            };
        }, []);

        return { menuHeaderRef };
    };

    const { menuHeaderRef } = useClickOutsideMenu();
    const { authentication: { token = "" } = {} } = useSelector<any, any>((state) => state);
    const { setCookie, removeCookie } = useCookies();
    const dispatch = useDispatch();

    const handleLogout = () => {
        dispatch(clearUserData());
        dispatch(deAuthenticate());
        removeCookie("st");
        toggleHamburgerMenu();
    };

    const isDev = process.env.NODE_ENV === "development";

    return (
        <NavContainer>
            <Dropdown ref={menuHeaderRef}>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start"
                    }}
                >
                    <LogoWrapper>
                        <a href="/">
                            <HeaderLogo>
                                <BrandLogo />
                            </HeaderLogo>
                        </a>
                    </LogoWrapper>
                    <MenusWrapper>
                        {menuItems
                            .filter((item) => !item.mobileOnly)
                            .map((item) => {
                                return (
                                    <Wrapper key={item.menuTitle}>
                                        <MenuHeader
                                            active={activeMenu === item.menuTitle}
                                            onClick={() => showDropdown(item.menuTitle)}
                                        >
                                            <StyledIcon>
                                                <SearchIcon />
                                            </StyledIcon>
                                            {item.menuTitle}
                                            <NavStyledArrow isOpen={activeMenu === item.menuTitle}>
                                                <DropdownIcon />
                                            </NavStyledArrow>
                                        </MenuHeader>
                                        <DropdownMenu
                                            change={activeMenu === item.menuTitle}
                                            data-testid="firstEl"
                                        >
                                            {item.dropdownItems.map((x) => {
                                                return (
                                                    <DropdownItem key={x.title}>
                                                        <DropdownLink href={x.link}>
                                                            {x.title}
                                                        </DropdownLink>
                                                    </DropdownItem>
                                                );
                                            })}
                                            {item.menuTitle === "Auto zoeken" && (
                                                <DownloadAppContainer>
                                                    <Divider mt="0.75rem" mb="0.75rem" />
                                                    <StyledLink href="https://autotrack.go.link/lLwJj">
                                                        <DownloadIconTextWrapper>
                                                            <IconWrapper>
                                                                <img
                                                                    alt="Download onze app"
                                                                    src="/_doa/static/images/download-app.webp"
                                                                />
                                                                <ArrowRight />
                                                            </IconWrapper>
                                                            <LinkText>Download onze app</LinkText>
                                                        </DownloadIconTextWrapper>
                                                    </StyledLink>
                                                </DownloadAppContainer>
                                            )}
                                        </DropdownMenu>
                                    </Wrapper>
                                );
                            })}
                    </MenusWrapper>
                </div>

                <AccountsContainer>
                    <Wrapper>
                        <MenuHeader
                            active={activeMenu === "Mijn AutoTrack"}
                            onClick={() => showDropdown("Mijn AutoTrack")}
                        >
                            <StyledIcon>
                                <SearchIcon />
                            </StyledIcon>
                            Mijn AutoTrack
                            <NavStyledArrow isOpen={activeMenu === "Mijn AutoTrack"}>
                                <DropdownIcon />
                            </NavStyledArrow>
                        </MenuHeader>
                        <DropdownMenu
                            change={activeMenu === "Mijn AutoTrack"}
                            data-testid="firstEl"
                        >
                            {token && (
                                <>
                                    <DropdownItem>
                                        <Link prefetch={false} passHref href="/mijn-autotrack">
                                            <DropdownLink>Accountgegevens</DropdownLink>
                                        </Link>
                                    </DropdownItem>
                                    <DropdownItem>
                                        <Link
                                            prefetch={false}
                                            passHref
                                            href="/mijn-autotrack/verkoop"
                                        >
                                            <DropdownLink>Auto verkopen</DropdownLink>
                                        </Link>
                                    </DropdownItem>
                                    <DropdownItem>
                                        <Link
                                            prefetch={false}
                                            passHref
                                            href="/mijn-autotrack/favorieten"
                                        >
                                            <DropdownLink>Favorieten</DropdownLink>
                                        </Link>
                                    </DropdownItem>
                                    <DropdownItem>
                                        <Link
                                            prefetch={false}
                                            passHref
                                            href="/mijn-autotrack/zoekservice"
                                        >
                                            <DropdownLink>Zoekopdrachten</DropdownLink>
                                        </Link>
                                    </DropdownItem>
                                    <DesktopHRLine />
                                </>
                            )}

                            <DropdownItem>
                                <Link
                                    prefetch={false}
                                    passHref
                                    href="https://analytics.autotrack.nl/"
                                >
                                    <DropdownLink>Login Zakelijk</DropdownLink>
                                </Link>
                            </DropdownItem>
                            {token && isDev && (
                                <DropdownItem>
                                    <DropdownLink
                                        href="/logout-local"
                                        rel="nofollow"
                                        onClick={handleLogout}
                                    />
                                </DropdownItem>
                            )}
                            {token && !isDev && (
                                <DropdownItem>
                                    <DropdownLink
                                        href="/mijn-autotrack/loguit"
                                        rel="nofollow"
                                        onClick={() => setCookie("uLi", "false")}
                                    >
                                        Uitloggen
                                    </DropdownLink>
                                </DropdownItem>
                            )}
                            {!token && (
                                <DropdownItem>
                                    <DropdownLink href="/login" rel="nofollow">
                                        Inloggen
                                    </DropdownLink>
                                </DropdownItem>
                            )}
                        </DropdownMenu>
                    </Wrapper>

                    {cookies.noAuthFav === "on" && token && (
                        <Wrapper className="hidden-md-down">
                            <Link prefetch={false} passHref href="/mijn-autotrack/favorieten">
                                <FavoriteLink rel="nofollow" awaiting={!!awaitingResponse?.length}>
                                    <StarIcon
                                        height={24}
                                        width={24}
                                        fill="#555e69"
                                        data-testid="nav-fav-icon"
                                    />
                                    {!!favouritesIdList?.length && (
                                        <Badge
                                            number={favouritesIdList?.length}
                                            testId="nav-fav-icon-count"
                                        />
                                    )}
                                </FavoriteLink>
                            </Link>
                        </Wrapper>
                    )}
                </AccountsContainer>
            </Dropdown>
            <MobileMenu open={hamburgerMenuClicked}>
                <MobileMenuMainWrapper>
                    {menuItems.map((item) => {
                        if (item.menuTitle === "Mijn Autotrack" && !token) return null;
                        return (
                            <MobileMenuContainer key={item.menuTitle}>
                                <MobileDropdownMenuHeader
                                    onClick={() => showDropdown(item.menuTitle)}
                                    active={activeMenu === item.menuTitle}
                                >
                                    {item.menuTitle}
                                    <NavStyledArrow isOpen={activeMenu === item.menuTitle}>
                                        <DropdownIcon />
                                    </NavStyledArrow>
                                </MobileDropdownMenuHeader>
                                <MobileDropdownMenu active={activeMenu === item.menuTitle}>
                                    {item.dropdownItems.map((menuItem) => {
                                        return (
                                            <Fragment key={menuItem.title}>
                                                <MobileDropdownItem
                                                    key={menuItem.title}
                                                    onClick={toggleHamburgerMenu}
                                                >
                                                    <a href={menuItem.link}>{menuItem.title}</a>
                                                </MobileDropdownItem>
                                            </Fragment>
                                        );
                                    })}
                                </MobileDropdownMenu>
                            </MobileMenuContainer>
                        );
                    })}

                    <div>
                        <Divider mt="1.5rem" mb="1.5rem" />
                        <StyledLink href="https://autotrack.go.link/1zPDF">
                            <DownloadIconTextWrapper>
                                <IconWrapper>
                                    <img
                                        alt="Download onze app"
                                        src="/_doa/static/images/download-app.webp"
                                    />
                                </IconWrapper>
                                <LinkText>Download onze app</LinkText>
                            </DownloadIconTextWrapper>
                        </StyledLink>
                    </div>
                </MobileMenuMainWrapper>
                <MobileAccountsBtnWrapper>
                    <MobileHRLine />
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            flexDirection: "column",
                            gap: "0.62rem",
                            padding: "0rem 1.5625rem"
                        }}
                    >
                        <MobileAuthButton href="https://analytics.autotrack.nl/">
                            Login Zakelijk
                        </MobileAuthButton>
                        {token && isDev && (
                            <MobileAuthButton href="/login" rel="nofollow" onClick={handleLogout}>
                                Uitloggen
                            </MobileAuthButton>
                        )}
                        {token && !isDev && (
                            <MobileAuthButton
                                href="/login"
                                rel="nofollow"
                                onClick={() => {
                                    setCookie("uLi", "false");
                                    toggleHamburgerMenu();
                                }}
                            >
                                Uitloggen
                            </MobileAuthButton>
                        )}
                        {!token && (
                            <MobileAuthButton href="/login" rel="nofollow">
                                Mijn AutoTrack
                            </MobileAuthButton>
                        )}
                    </div>
                </MobileAccountsBtnWrapper>
            </MobileMenu>
            <a href="/">
                <HeaderLogoMobile>
                    <BrandLogo />
                </HeaderLogoMobile>
            </a>
            <HamburgerMenuContainer onClick={activateHamburgerMenu}>
                <HamburgerMenu change={hamburgerMenuClicked} />
            </HamburgerMenuContainer>
        </NavContainer>
    );
};
